import React, { useState } from 'react'
import styled from 'styled-components'
import { axios } from '../../context/auth'
import { PrimaryButton } from '../../styled/button'
import ReactSelect from '../../styled/select'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.bodyText};
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  border: 1px solid ${({ theme }) => theme.borderColor};
`

const ModalTitle = styled.h2`
  color: ${({ theme }) => theme.bodyText};
  margin-top: 0;
`

const FormGroup = styled.div`
  margin-bottom: 1rem;
`

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.bodyText};
`

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.inputBackground};
  color: ${({ theme }) => theme.input};
  box-sizing: border-box;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.tertiary};
  margin-bottom: 1rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.secondaryBackground};
  color: ${({ theme }) => theme.bodyText};
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const InviteUserModal = ({ contentType, contentId, onClose, onSuccess }) => {
  const [email, setEmail] = useState('')
  const [permissionLevel, setPermissionLevel] = useState(contentType === 'group' ? 'MEMBER' : 'VIEWER')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)
      await axios.post('/api/invitations', {
        email,
        contentType,
        contentId,
        permissionLevel
      })
      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (err) {
      console.error('Error sending invitation:', err)
      if (err.response?.data?.msg) {
        setError(err.response.data.msg)
      } else {
        setError('Failed to send invitation')
      }
    } finally {
      setLoading(false)
    }
  }

  // Determine permission options based on content type
  const permissionOptions = contentType === 'group'
    ? [
        { value: 'MEMBER', label: 'Member' },
        { value: 'OWNER', label: 'Owner' }
      ]
    : [
        { value: 'VIEWER', label: 'Viewer' },
        { value: 'EDITOR', label: 'Editor' },
        { value: 'OWNER', label: 'Owner' }
      ]

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>Invite User</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor='email'>Email Address</Label>
            <Input
              id='email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='user@example.com'
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor='permission'>Permission Level</Label>
            <ReactSelect
              value={{ value: permissionLevel, label: permissionOptions.find(opt => opt.value === permissionLevel)?.label || permissionLevel }}
              onChange={(option) => setPermissionLevel(option.value)}
              options={permissionOptions}
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <CancelButton type='button' onClick={onClose} disabled={loading}>
              Cancel
            </CancelButton>
            <PrimaryButton type='submit' disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </PrimaryButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>
  )
}

export default InviteUserModal
